// File: Institutional_trend.jsx

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { List, ListItem, ListItemText, Divider, Box, Typography, Dialog, DialogTitle, DialogContent, Alert } from '@mui/material';
import InfoTooltip from './InfoTooltip';
import api from '../functions/api';

import ReactMarkdown from 'react-markdown';

import rehypeRaw from 'rehype-raw';


const Institutional_Trends_Table = () => {
    const { columns = [], rows = [] } = useSelector(
        state => state.portfolio?.data?.Vector_institutional_trend || {}
      );
      
      // console.log('Institutional Trends - columns:', columns);
      // console.log('Institutional Trends - rows:', rows);


  const [selectedVector, setSelectedVector] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [open, setOpen] = useState(false);

  if (!rows || rows.length === 0 || !columns || columns.length === 0) {
    return <div>No data available</div>;
  }

  // Check if all institutional trend signals are 'Neutral'
  const checkAllInstitutionalTrendsNeutral = () => {
    const row = rows[0]; // Take the first (most recent) row
    
    if (!row || columns.length <= 2) return false;
    
    // Check if all institutional trend signals are 'Neutral'
    const allNeutral = columns.slice(2).every((column, idx) => {
      const signal = row[idx + 2]; // +2 to account for ID and epoch columns
      return signal === 'Neutral';
    });
    
    return allNeutral;
  };
  

  const getSignalCategory = (value) => {
    if (typeof value !== 'string') return 'Neutral';
  
    if (value.includes('Strong Buy')) return 'Strong Buying';
    if (value.includes('Weak Buy')) return 'Weak Buying';
    if (value.includes('Weak Sell')) return 'Weak Selling';
    if (value.includes('Strong Sell')) return 'Strong Selling';

  
    return 'Neutral';
  };
  
  const getRowColor = (value) => {
    const category = getSignalCategory(value);
    switch (category) {
      case 'Strong Buying': return '#DFF2BF';
      case 'Weak Buying': return '#E6F5D0';
      case 'Weak Selling': return '#FEEFB3';
      case 'Strong Selling': return '#FF4C4C';
      default: return 'transparent';
    }
  };
  

  const categoryCount = {
    'Strong Buying': 0,
    'Weak Buying': 0,
    'Weak Selling': 0,
    'Strong Selling': 0,
    'Neutral': 0
  };
  
  columns.forEach((col, cidx) => {
    if (col === "ID" || col === "epoch") return;
    const value = rows[0]?.[cidx]; // assuming single-row
    const category = getSignalCategory(value);
    if (category in categoryCount) {
      categoryCount[category]++;
    } else {
      categoryCount['Neutral']++;
    }
  });
  
  // console.log('Institutional Trends Signal Categories:', categoryCount);
  
  
  const handleRowClick = async (vectorName) => {
    setSelectedVector(vectorName);
    setOpen(true);
  
    try {
      const analysis_type = "Institutional_trend";
      const response = await api.post("/Market_analysis", { analysis_type });
      // console.log('Analysis response:', response.data);
      setAnalysisData(response.data);
    } catch (error) {
      setAnalysisData({ error: "Failed to retrieve analysis" });
    }
  };
  
  

  return (
    <>
      {checkAllInstitutionalTrendsNeutral() && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: '0.9rem' }}>
          ⏳ Waiting for new NAV calculation
        </Alert>
      )}
      
      <TableContainer component={Paper} sx={{ width: 'fit-content', overflow: 'auto', padding: '2px' }}>
        <Table size="small" stickyHeader>
        <TableHead>
            <TableRow>
              <TableCell 
                sx={{ fontSize: '14px', fontWeight: 'bold', padding: '0.8px', cursor: 'pointer' }}
                onClick={() => handleRowClick('Market')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Institutional Trends
                  <InfoTooltip 
                    text="ETF-based institutional flow analysis using 30-day A/D Line and OBV indicators, with 90-180 day confirmation windows and 120-day flow deltas to detect accumulation/distribution patterns"
                    placement="top"
                    size="small"
                  />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
              {Object.entries(categoryCount).map(([category, count], idx) => (
                <TableRow
                  key={idx}
                  onClick={() => handleRowClick(category)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    backgroundColor: getRowColor(category)
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: '12px',
                      padding: '0.2px',
                      whiteSpace: 'normal',
                      fontWeight: 'bold',
                      minWidth: '200px'
                    }}
                  >
                    {category}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      padding: '0.2px',
                      whiteSpace: 'normal'
                    }}
                  >
                    {count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>



        </Table>
      </TableContainer>
  
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "90vw",
            maxWidth: "1200px",
            minWidth: "300px",
            overflowX: "hidden"
          }
        }}
      >
        <DialogTitle>{selectedVector} Analysis</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            width: "100%",
            padding: "16px"
          }}
        >
          {analysisData ? (
            <Box sx={{ width: "100%" }}>
              {analysisData.purpose && (
                <>
                  <Typography variant="h6" gutterBottom><strong>Result:</strong></Typography>
                  <Box sx={{
                    display: "block",
                    width: "100%",
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                    lineBreak: "anywhere"
                  }}>
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
  {analysisData.result || "N/A"}
</ReactMarkdown>

                    
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </>
              )}
  
              <Typography variant="h6" gutterBottom><strong>Purpose:</strong></Typography>
              <Box sx={{
                display: "block",
                width: "100%",
                maxWidth: "100%",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                lineBreak: "anywhere"
              }}>
                <ReactMarkdown>{analysisData.purpose}</ReactMarkdown>
                
              </Box>
  
              <Divider sx={{ my: 2 }} />
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
  
};

export default Institutional_Trends_Table;
